// src/modules/ida/hooks/useJobCompletionRefresh.ts
//
// Watches background jobs for a completed job that requests a context or
// workspace refresh and fires the matching callbacks. Extracted from Chat.tsx
// so the refresh-trigger logic is unit-testable without rendering the full
// chat surface.
//
// Task #47: when workspace settings are persisted (refresh_workspace), the BE
// has just cleared the computed needs_onboarding flag. The onboarding-config
// refetch MUST fire here so the FE stops sending the stale X-Onboarding-Mode
// header immediately, instead of keeping an admin on the onboarding code path
// until a remount.

import { useEffect } from "react";

interface JobRefreshFlags {
  refresh_context?: boolean;
  refresh_workspace?: boolean;
}

/** Minimal shape this hook reads off a background job. */
interface JobLike {
  status?: string;
  userDisplay?: unknown;
}

export interface JobCompletionRefreshCallbacks {
  /** Fired when a completed job requests refresh_context OR refresh_workspace. */
  onRefreshUIConfig: () => void;
  /** Fired only on refresh_workspace (workspace settings persisted). */
  onRefreshWorkspace: () => void;
  /**
   * Fired only on refresh_workspace. Re-fetches onboarding config so the stale
   * X-Onboarding-Mode header stops being sent once the BE clears
   * needs_onboarding (Task #47).
   */
  onRefreshOnboardingConfig: () => void;
}

/** Read the refresh flags off a job's userDisplay payload (defensive). */
export function readJobRefreshFlags(userDisplay: unknown): Required<JobRefreshFlags> {
  const flags = (userDisplay as JobRefreshFlags | undefined) || {};
  return {
    refresh_context: Boolean(flags.refresh_context),
    refresh_workspace: Boolean(flags.refresh_workspace),
  };
}

export function useJobCompletionRefresh(
  backgroundJobs: JobLike[],
  callbacks: JobCompletionRefreshCallbacks,
): void {
  const { onRefreshUIConfig, onRefreshWorkspace, onRefreshOnboardingConfig } =
    callbacks;
  useEffect(() => {
    const completedJobWithRefresh = backgroundJobs.find((job) => {
      if (job.status !== "completed") return false;
      const { refresh_context, refresh_workspace } = readJobRefreshFlags(
        job.userDisplay,
      );
      return refresh_context || refresh_workspace;
    });
    if (!completedJobWithRefresh) return;

    onRefreshUIConfig();
    // Workspace settings were updated -> refresh workspace data and clear the
    // now-stale onboarding state/header (Task #47).
    if (readJobRefreshFlags(completedJobWithRefresh.userDisplay).refresh_workspace) {
      onRefreshWorkspace();
      onRefreshOnboardingConfig();
    }
  }, [
    backgroundJobs,
    onRefreshUIConfig,
    onRefreshWorkspace,
    onRefreshOnboardingConfig,
  ]);
}
